import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { IGradeFormValues } from '../../../../../@types/model/masterData/grade/gradeFormValues';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import GradeModelHelper from '../../../../../@types/model/masterData/grade/gradeModelHelper';
import { useAppSelector } from '../../../../../@types/redux';
import OptionType from '../../../../../@types/model/optionType';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface IGradeFormProps {
    isLoading : boolean;
    initialValues : IGradeFormValues;
    onSubmit : (values : IGradeFormValues) => void;
    onClose : () => void;
}

const GradeForm = (props : IGradeFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IGradeFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(GradeModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities.filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [commodities]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Code'}
                        name={'code'}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10'}
                        control={control}
                        label={'Name'}
                        name={'name'}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'mb10'}
                        control={control}
                        label={'Commodities'}
                        defaultValues={props.initialValues.commodities}
                        options={commodityOptions}
                        isMulti
                        name={'commodities'}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox 
                            name={'isActive'}
                            label={'Is Active?'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe mb20'}>
                        <Button 
                            className={'cpd mr20'}
                            onClick={props.onClose} 
                            type={'reset'}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default GradeForm;
